// 後台手動報名：可先搜尋既有會員帶入資料，或直接輸入現場/電話報名者（沒有 LINE 也可以）
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';

const memberLabel = (u) => u ? `${u.realName || u.name || ''}${u.displayName ? `（${u.displayName}）` : ''}` : '';
const EMPTY_FORM = { name: '', mobile: '', community: 'IMC', department: '', position: '', other: '' };

export default function ManualRegistrationDialog({ open, onClose, onCreated, productId, productName, options }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState(EMPTY_FORM);
  const [member, setMember] = useState(null);
  const [memberInput, setMemberInput] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [oneimc, setOneimc] = useState(null);
  const [oneimcLoading, setOneimcLoading] = useState(false);
  const [sendTicket, setSendTicket] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [duplicates, setDuplicates] = useState(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setMember(null);
      setMemberInput('');
      setMemberOptions([]);
      setOneimc(null);
      setSendTicket(true);
      setError('');
      setDuplicates(null);
    }
  }, [open]);

  // 會員搜尋（輸入停頓 300ms 才查）
  useEffect(() => {
    clearTimeout(searchTimer.current);
    const q = memberInput.trim();
    if (!q || (member && memberInput === memberLabel(member))) return;
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await fetch(`/api/orders/manual/lookup?q=${encodeURIComponent(q)}&productId=${productId}`, { credentials: 'include' });
        setMemberOptions(r.ok ? await r.json() : []);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [memberInput, member, productId]);

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm(f => ({ ...f, [field]: value }));
    setDuplicates(null);
  };

  const pickMember = (u) => {
    setMember(u);
    setOneimc(null);
    setDuplicates(null);
    if (u) {
      setForm(f => ({
        ...f,
        name: u.realName || u.name || '',
        mobile: u.mobile || '',
        community: u.community || f.community,
        department: u.department || '',
        position: u.position || ''
      }));
    }
  };

  const lookupOneimc = async () => {
    if (!form.name.trim()) return;
    setOneimcLoading(true);
    try {
      const params = new URLSearchParams({ name: form.name.trim() });
      if (form.department && form.department !== '一般民眾') params.set('chapter', form.department);
      let r = await fetch(`/api/orders/manual/oneimc?${params}`, { credentials: 'include' });
      let data = r.ok ? await r.json() : { found: false };
      if (!data.found && params.has('chapter')) {
        r = await fetch(`/api/orders/manual/oneimc?name=${encodeURIComponent(form.name.trim())}`, { credentials: 'include' });
        data = r.ok ? await r.json() : { found: false };
      }
      setOneimc(data);
      if (data.found) {
        setForm(f => ({
          ...f,
          community: 'IMC',
          department: data.chapter || f.department,
          position: data.title || f.position,
          mobile: f.mobile || data.mobile || ''
        }));
      }
    } finally {
      setOneimcLoading(false);
    }
  };

  const submit = async (force = false) => {
    setSubmitting(true);
    setError('');
    try {
      const r = await fetch('/api/orders/manual', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, productId, userId: member?.id, sendTicket: sendTicket && member?.hasLine, force })
      });
      const data = await r.json();
      if (r.status === 409 && data.possibleDuplicates) {
        setDuplicates(data.possibleDuplicates);
        return;
      }
      if (!r.ok) throw new Error(data.error || '報名失敗');
      onCreated?.(data, form.name);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const alreadyRegistered = member?.registeredOrderId;
  const canSubmit = form.name.trim() && !submitting && !alreadyRegistered;

  const freeSelect = (field, label, list) => (
    <Autocomplete
      freeSolo
      options={list || []}
      value={form[field]}
      inputValue={form[field]}
      onInputChange={(e, v) => { setForm(f => ({ ...f, [field]: v })); setDuplicates(null); }}
      renderInput={(params) => <TextField {...params} label={label} size="small" fullWidth />}
    />
  );

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        手動報名
        <Typography variant="body2" color="text.secondary">{productName}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Autocomplete
          options={memberOptions}
          value={member}
          onChange={(e, u) => pickMember(u)}
          inputValue={memberInput}
          onInputChange={(e, v) => setMemberInput(v)}
          filterOptions={(x) => x}
          getOptionLabel={memberLabel}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          loading={searching}
          noOptionsText={memberInput ? '找不到會員，可直接在下方填寫' : '輸入姓名、LINE 名稱或手機'}
          renderOption={(props, u) => (
            <Box component="li" {...props} key={u.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.realName || u.name}</Typography>
                {u.displayName && <Typography variant="caption" color="text.secondary">LINE：{u.displayName}</Typography>}
                {u.registeredOrderId && <Chip size="small" color="warning" label="已報名" />}
                {!u.hasLine && <Chip size="small" variant="outlined" label="無 LINE" />}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {[u.mobile, u.community, u.department, u.position].filter(Boolean).join('・') || '無其他資料'}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="搜尋既有會員（選填）"
              placeholder="姓名、LINE 名稱或手機"
              size="small"
              helperText="選了會員就會用他的帳號報名、可發 LINE 電子票；不選則以下方資料建立新報名"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (<>{searching && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>)
                }
              }}
            />
          )}
        />

        {alreadyRegistered && (
          <Alert severity="warning" sx={{ mt: 2 }}>此會員已報名本活動（報名編號 {alreadyRegistered}），不需要再報名。</Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="姓名" required size="small" fullWidth value={form.name} onChange={set('name')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="手機" size="small" fullWidth value={form.mobile} onChange={set('mobile')}
              placeholder="09xxxxxxxx" slotProps={{ htmlInput: { inputMode: 'tel' } }} />
          </Grid>
          <Grid size={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" onClick={lookupOneimc} disabled={!form.name.trim() || oneimcLoading}
                startIcon={oneimcLoading ? <CircularProgress size={14} /> : null}>
                從 oneimc 帶入社別／社職
              </Button>
              {oneimc && (
                <Typography variant="caption" color={oneimc.found ? 'success.main' : 'text.secondary'}>
                  {oneimc.found ? `已帶入：${oneimc.chapter}／${oneimc.title}` : 'oneimc 查無此人（或同名多筆），請手動填寫'}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>{freeSelect('community', '社團', options?.community)}</Grid>
          <Grid size={{ xs: 12, sm: 4 }}>{freeSelect('department', '社別', options?.department)}</Grid>
          <Grid size={{ xs: 12, sm: 4 }}>{freeSelect('position', '社職', options?.position)}</Grid>
          <Grid size={12}>
            <TextField label="備註" size="small" fullWidth value={form.other} onChange={set('other')} placeholder="例如：電話報名、現場報名、代某某報名" />
          </Grid>
        </Grid>

        <FormControlLabel
          sx={{ mt: 1 }}
          control={<Checkbox checked={sendTicket && !!member?.hasLine} onChange={(e) => setSendTicket(e.target.checked)} disabled={!member?.hasLine} />}
          label={member?.hasLine ? '報名後用 LINE 推送電子票給此會員' : '發送 LINE 電子票（需先選擇有 LINE 的會員）'}
        />

        {duplicates && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            本活動已有相同姓名或手機的報名，可能重複：
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {duplicates.map(d => (
                <li key={d.id}>#{d.id} {d.name} {d.mobile} {d.lineName ? `（LINE：${d.lineName}）` : ''}</li>
              ))}
            </Box>
            確定是不同人，再按「仍要報名」。
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>取消</Button>
        {duplicates ? (
          <Button variant="contained" color="warning" onClick={() => submit(true)} disabled={!canSubmit}>仍要報名</Button>
        ) : (
          <Button variant="contained" onClick={() => submit(false)} disabled={!canSubmit}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            確認報名
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
